import type Phaser from 'phaser';
import i18next from 'i18next';
import { WEB_FREE_STARS, isWebPlayLocked } from '@/game/promo';
import { showPromoCard } from '@/game/ui/promoCard';
import { createStoreBadges } from '@/game/ui/storeBadges';
import { createCardQr } from '@/game/ui/cardQr';
import { isDesktopBrowser } from '@/game/qrPromo';
import { getActivePack, packKey } from '@/game/packs';

/**
 * The end of the free web build: the player has collected `WEB_FREE_STARS` journey stars, and from
 * here the game continues in the paid app (src/game/promo.ts `isWebPlayLocked`).
 *
 * Desktop gets the QR code beside the text — the laptop cannot install a phone app, the phone in
 * the parent's hand can — and phones and tablets get the store badges, because nobody scans their
 * own screen (the same split as the demo's outro card, src/game/ui/clipOutro.ts). The code points
 * at /get, where the scanning phone picks its own store.
 *
 * Not dismissible into play: "Not now" closes the card, but every game start opens it again.
 */
export function showWebLimitCard(scene: Phaser.Scene, opts: { delayMs?: number } = {}): void {
  const withQr = isDesktopBrowser();
  showPromoCard(scene, {
    title: i18next.t('webLimit.title'),
    lines: [
      i18next.t('webLimit.reached', { count: WEB_FREE_STARS }),
      i18next.t(withQr ? 'webLimit.scan' : 'webLimit.store'),
      i18next.t('webLimit.trust'),
    ],
    extra: withQr ? createCardQr() : createStoreBadges({ height: 54 }),
    actions: [{ label: i18next.t('buy.later'), kind: 'ghost' }],
  }, {
    delayMs: opts.delayMs ?? 0,
    size: 'lg',
    className: withQr ? 'promo-card--qr-side' : undefined,
  });
}

/**
 * The gate every game scene runs first thing in `create()`: when the free web build has run out,
 * go back to the Menu, which shows the card. Returns true when it redirected — the caller must then
 * return without building the scene.
 *
 * In the scenes rather than on the buttons so that every route in is covered at once: Play, the
 * reward minigames, Replay on the end screens, and any dev link straight into a scene.
 */
export function redirectIfWebLimit(scene: Phaser.Scene): boolean {
  if (!isWebPlayLocked(packKey(getActivePack()))) return false;
  scene.scene.start('Menu', { webLimit: true });
  return true;
}
